<template>
	<v-navigation-drawer
		:model-value="modelValue"
		@update:model-value="$emit('update:modelValue', $event)"
		location="end"
		:width="width"
		permanent
		disable-resize-watcher
		:scrim="false"
		class="CopilotDrawer"
		:style="{ '--copilot-bottom-inset': `${bottomInset}px` }"
		data-cy="copilot-drawer"
		aria-label="Copilot"
	>
		<div class="CopilotDrawer__header">
			<v-icon class="mr-2">mdi-creation</v-icon>
			<span class="CopilotDrawer__title">{{ $t('text.copilotTitle') }}</span>
			<v-spacer />
			<v-btn v-if="developerMode" variant="text" size="small" prepend-icon="mdi-flask-outline" data-cy="copilot-load-fixture" @click="$emit('load-fixture')">
				{{ $t('text.copilotLoadFixture') }}
			</v-btn>
			<v-btn icon="mdi-close" variant="text" size="small" data-cy="copilot-close" :aria-label="$t('text.close')" @click="close" />
		</div>

		<div class="CopilotDrawer__conversation" :class="{ 'CopilotDrawer__conversation--dragging': dragging }"
			v-bind="dropZone"
		>
			<div v-if="dragging" class="CopilotDrawer__dropHint" data-cy="copilot-drop-hint">{{ $t('text.copilotAttachDropHint') }}</div>
			<div v-if="showEmptyHint" class="CopilotDrawer__hint" data-cy="copilot-empty-hint">
				<v-icon size="32" class="mb-2">mdi-creation</v-icon>
				<p>{{ $t('text.copilotEmptyHint') }}</p>
				<p v-if="features?.url_fetch">{{ $t('text.copilotEmptyHintUrl') }}</p>
				<p v-if="attachmentsOffered">{{ $t('text.copilotEmptyHintAttachments') }}</p>
			</div>
			<div v-for="(message, index) of messages" :key="index" class="CopilotDrawer__message" :class="`CopilotDrawer__message--${message.role}`" data-cy="copilot-message">
				{{ message.text }}
			</div>

			<CopilotTimeline :steps="steps" :busy="busy" :pendingLabel="$t(`text.${workingKey}`)" />

			<div v-if="groups.length" class="CopilotDrawer__proposal" data-cy="copilot-proposal">
				<div class="CopilotDrawer__proposalHeader">
					<span class="CopilotDrawer__proposalTitle">{{ $t('text.copilotProposalTitle') }}</span>
					<div class="CopilotDrawer__proposalActions">
						<v-btn size="small" variant="text" class="text-none" prepend-icon="mdi-check-all" :disabled="!hasOpenEntries" data-cy="copilot-accept-all" @click="$emit('accept-all')">{{ $t('text.copilotAcceptAll') }}</v-btn>
						<v-btn size="small" variant="text" class="text-none" prepend-icon="mdi-undo" :disabled="!hasAppliedEntries" data-cy="copilot-revert-all" @click="$emit('revert-all')">{{ $t('text.copilotRevertAll') }}</v-btn>
					</div>
				</div>

				<div v-for="group of groups" :key="group.section" class="CopilotDrawer__group" data-cy="copilot-group">
					<div class="CopilotDrawer__groupTitle">{{ sectionTitle(group) }}</div>
					<div v-for="entry of group.entries" :key="entry.id"
						class="CopilotDrawer__entry" :class="[ `CopilotDrawer__entry--${entry.status}`, { 'CopilotDrawer__entry--error': entry.errors.length } ]"
						data-cy="copilot-entry" :data-entry-id="entry.id" :data-status="entry.status"
					>
						<button type="button" class="CopilotDrawer__entryMain" data-cy="copilot-entry-jump" @click="$emit('jump', entry.id)">
							<span class="CopilotDrawer__entryField">{{ fieldTitle(entry) }}</span>
							<span class="CopilotDrawer__entryStatus">{{ $t(`text.copilotStatus_${entry.status}`) }}</span>
							<span v-if="isAssumption(entry)" class="CopilotDrawer__entryAssumption" data-cy="copilot-entry-assumption">{{ $t('text.copilotAssumption') }}</span>
							<span class="CopilotDrawer__entryValue">
								<template v-if="entry.op === 'set'">
									<template v-if="entry.status === 'pending' || entry.status === 'accepted'">
										<s v-if="hasValue(entry.previous)">{{ display(entry.previous) }}</s>
										<span v-if="hasValue(entry.previous)"> → </span>
									</template>
									{{ display(entry.value) }}
								</template>
								<template v-else-if="entry.op === 'append'">{{ $t('text.copilotOpAppend') }}: {{ display(entry.value) }}</template>
								<template v-else>{{ $t('text.copilotOpRemove') }}: {{ display(entry.previous) }}</template>
							</span>
							<span class="CopilotDrawer__entryReason">{{ entry.reason }}</span>
							<span v-for="(error, index) of entry.errors" :key="index" class="CopilotDrawer__entryError" data-cy="copilot-entry-error">{{ error }}</span>
						</button>
						<div class="CopilotDrawer__entryActions">
							<v-btn v-if="entry.status !== 'accepted'" icon="mdi-check" size="x-small" variant="text" :aria-label="$t('text.copilotAccept')" data-cy="copilot-entry-accept" @click="$emit('accept', entry.id)" />
							<v-btn v-if="entry.status !== 'reverted'" icon="mdi-undo" size="x-small" variant="text" :aria-label="$t('text.copilotRevert')" data-cy="copilot-entry-revert" @click="$emit('revert', entry.id)" />
						</div>
					</div>
				</div>

				<div v-if="dropped.length" class="CopilotDrawer__dropped" data-cy="copilot-dropped">
					<div class="CopilotDrawer__groupTitle">{{ $t('text.copilotDroppedTitle') }}</div>
					<div v-for="(drop, index) of dropped" :key="index" class="CopilotDrawer__droppedItem">
						<code>{{ drop.operation.path }}</code> — {{ $t(`text.copilotDrop_${drop.reason}`) }}
					</div>
				</div>
			</div>
		</div>

		<div class="CopilotDrawer__composer" v-bind="dropZone">
			<CopilotAttachments v-if="attachmentsOffered" ref="attachments"
				:attachments="attachments" :rejected="rejectedFiles" :disabled="busy"
				@add-files="$emit('attach-files', $event)" @remove="$emit('remove-attachment', $event)"
				@toggle-images="(id, on) => $emit('toggle-attachment-images', id, on)"
			/>
			<div class="CopilotDrawer__box" data-cy="copilot-composer-box" @click.self="focusInput">
				<v-textarea
					ref="input"
					v-model="draft"
					variant="plain"
					density="compact"
					rows="1"
					max-rows="8"
					auto-grow
					hide-details
					class="CopilotDrawer__input"
					:placeholder="$t('text.copilotInputPlaceholder')"
					data-cy="copilot-input"
					@keydown.enter.exact.prevent="send"
				/>
				<div class="CopilotDrawer__tools" @click.self="focusInput">
					<v-btn v-if="attachmentsOffered" icon="mdi-paperclip" variant="text" size="small" :disabled="busy"
						:aria-label="$t('text.copilotAttach')" :title="$t('text.copilotAttach')" data-cy="copilot-attach" @click="$refs.attachments?.open()"
					/>
					<v-spacer />
					<CopilotMic v-if="features?.transcribe && transcribe" :transcribe="transcribe" :maxSeconds="features.transcribe_max_seconds || 120" :disabled="busy" @transcript="insertTranscript" />
					<v-btn v-if="busy" icon="mdi-stop" size="small" variant="flat" color="grey-darken-3"
						:aria-label="$t('text.cancel')" :title="$t('text.cancel')" data-cy="copilot-cancel" @click="$emit('cancel')"
					/>
					<v-btn v-else icon="mdi-arrow-up" size="small" variant="flat" color="primary" :disabled="!canSend"
						:aria-label="$t('text.copilotSend')" :title="$t('text.copilotSend')" data-cy="copilot-send" @click="send"
					/>
				</div>
			</div>
		</div>
	</v-navigation-drawer>
</template>

<script lang="js">
// The Copilot drawer: conversation, the Proposal review list (grouped by section) and the composer
// with the attachments of the next turn (chips, file picker, drag-and-drop over the drawer body).
// It owns no entity state; every review and attachment action is emitted and handled by the page adapter.
// Voice input (ticket 11): the microphone (CopilotMic) shows when the features say transcription
// is on and the page handed in a `transcribe` call; the text lands at the cursor, never sent by itself.
import CopilotAttachments from './CopilotAttachments.vue'
import CopilotMic from './CopilotMic.vue'
import CopilotTimeline from './CopilotTimeline.vue'
import { displayValue, fieldTitle, hasValue, isAssumption, translateFirst } from '@/copilot/copilot-display'

const APPLIED = [ 'proposed', 'accepted' ]
const URL_PATTERN = /https?:\/\/\S/i
const OPEN = [ 'proposed', 'pending' ]

export default {
	name: 'CopilotDrawer',
	components: { CopilotAttachments, CopilotMic, CopilotTimeline },
	props: {
		modelValue: { type: Boolean, default: false },
		isNewService: { type: Boolean, default: false },
		messages: { type: Array, default: () => [] },
		/** ReviewState of copilot/proposal-apply.ts */
		review: { type: Object, default: () => ({ entries: [] }) },
		/** DroppedOperation[] of the last apply */
		dropped: { type: Array, default: () => [] },
		developerMode: { type: Boolean, default: false },
		/** TrajectoryStep[] of the running or last run (copilot-client.ts), rendered as a collapsed timeline */
		steps: { type: Array, default: () => [] },
		/** a run is streaming: send is disabled, cancel is offered */
		busy: { type: Boolean, default: false },
		/** GET /ai-copilot/features; what is off stays hidden */
		features: { type: Object, default: null },
		/** (blob: Blob) => Promise<string>: the page's transcribe call with the user's token; without it no microphone */
		transcribe: { type: Function, default: null },
		/** PendingAttachment[] of copilot/copilot-attachments.ts: the files of the next turn */
		attachments: { type: Array, default: () => [] },
		/** RejectedFile[] of the last add (too many, too large, duplicate) */
		rejectedFiles: { type: Array, default: () => [] },
		/** px; the page reserves the same width for the drawer (ServiceDesigner `copilotDocked`) */
		width: { type: Number, default: 440 },
		/** px kept free at the bottom of the viewport, e.g. the 24 px impersonation bar */
		bottomInset: { type: Number, default: 0 },
	},
	emits: [
		'update:modelValue', 'send', 'cancel', 'accept-all', 'revert-all', 'accept', 'revert', 'jump', 'load-fixture',
		'attach-files', 'remove-attachment', 'toggle-attachment-images',
	],
	data: () => ({
		draft: '',
		dragging: false,
	}),
	computed: {
		entries() {
			return this.review?.entries ?? []
		},
		groups() {
			const groups = []
			for (const entry of this.entries) {
				let group = groups.find(g => g.section === entry.section)
				if (!group) groups.push(group = { section: entry.section, label: entry.sectionLabel, entries: [] })
				group.entries.push(entry)
			}
			return groups
		},
		hasOpenEntries() {
			return this.entries.some(e => OPEN.includes(e.status))
		},
		hasAppliedEntries() {
			return this.entries.some(e => APPLIED.includes(e.status))
		},
		showEmptyHint() {
			return this.isNewService && this.messages.length === 0
		},
		attachmentsOffered() {
			return this.features?.attachments === true
		},
		// what the drawer says while the run has no running tool call (before the first one too): known from
		// the turn itself, so it shows the moment the user sends
		workingKey() {
			const last = [ ...this.messages ].reverse().find(message => message.role === 'user')
			if (last && URL_PATTERN.test(last.text ?? '')) return 'copilotWorkingUrl'
			if (this.attachments.length) return 'copilotWorkingAttachment'
			return 'copilotWorkingThinking'
		},
		// a turn needs text or at least one attachment
		canSend() {
			return !this.busy && (this.draft.trim().length > 0 || this.attachments.length > 0)
		},
		// drag-and-drop listeners, bound on the conversation and the composer
		dropZone() {
			if (!this.attachmentsOffered) return {}
			return {
				'data-cy': 'copilot-dropzone',
				onDragenter: this.onDragOver,
				onDragover: this.onDragOver,
				onDragleave: this.onDragLeave,
				onDrop: this.onDrop,
			}
		},
	},
	watch: {
		modelValue: {
			immediate: true,
			handler(open) {
				if (open) document.addEventListener('keydown', this.onKeydown)
				else document.removeEventListener('keydown', this.onKeydown)
			},
		},
	},
	beforeUnmount() {
		document.removeEventListener('keydown', this.onKeydown)
	},
	methods: {
		close() {
			this.$emit('update:modelValue', false)
		},
		onKeydown(event) {
			if (event.key === 'Escape') this.close()
		},
		focusInput() {
			this.$refs.input?.focus?.()
		},
		send() {
			if (!this.canSend) return
			this.$emit('send', this.draft.trim())
			this.draft = ''
		},
		// The transcript goes in at the cursor (or over the selection), with a space on either side
		// where the neighbours need one; the caret ends after it. Nothing is sent.
		insertTranscript(text) {
			const textarea = this.$refs.input?.$el?.querySelector('textarea')
			const value = this.draft
			const start = textarea?.selectionStart ?? value.length
			const end = textarea?.selectionEnd ?? value.length
			const before = value.slice(0, start)
			const after = value.slice(end)
			const piece = (before && !/\s$/.test(before) ? ' ' : '') + text + (after && !/^[\s.,;:!?]/.test(after) ? ' ' : '')
			this.draft = before + piece + after
			this.$nextTick(() => {
				if (!textarea) return
				textarea.focus()
				textarea.setSelectionRange(before.length + piece.length, before.length + piece.length)
			})
		},
		onDragOver(event) {
			if (this.busy) return
			event.preventDefault()
			this.dragging = true
		},
		onDragLeave(event) {
			if (!event.currentTarget?.contains?.(event.relatedTarget)) this.dragging = false
		},
		onDrop(event) {
			event.preventDefault()
			this.dragging = false
			if (this.busy) return
			const files = Array.from(event.dataTransfer?.files ?? [])
			if (files.length) this.$emit('attach-files', files)
		},
		sectionTitle(group) {
			return translateFirst(key => this.$t(key), [ `${group.label}Title`, `${group.section}Title`, group.label ], group.label)
		},
		fieldTitle(entry) {
			return fieldTitle(key => this.$t(key), entry)
		},
		hasValue,
		isAssumption,
		display: displayValue,
	},
}
</script>

<style lang="scss">
/* Marker on the page for fields holding a proposed value (set by the page adapter). */
.copilot-proposed {
	position: relative;
	outline: 2px solid #7e57c2;
	outline-offset: 3px;
	border-radius: 4px;

	&::before {
		content: 'Copilot';
		position: absolute;
		top: -10px;
		right: 8px;
		z-index: 1;
		padding: 0 6px;
		border-radius: 8px;
		background: #7e57c2;
		color: white;
		font-size: 10px;
		line-height: 16px;
		font-weight: 600;
		letter-spacing: 0.02em;
	}
}

/* A collapsed section has no rendered fields: the marker sits on the section's card (the white panel)
   and is drawn outside it like a field's, in the 10 px gap between the cards. The section's panels
   wrapper is lifted above its neighbours (z-index 1 each, the later one painting over the earlier),
   so neither the outline nor the badge ends up behind the next card. */
.copilot-proposed-host {
	z-index: 2 !important;
}
</style>

<style scoped lang="scss">
.CopilotDrawer {
	top: 64px !important;
	height: calc(100% - 64px - var(--copilot-bottom-inset, 0px)) !important;
	z-index: 100 !important;
	display: flex;
	flex-direction: column;

	:deep(.v-navigation-drawer__content) {
		display: flex;
		flex-direction: column;
		height: 100%;
	}

	&__header {
		display: flex;
		align-items: center;
		flex: 0 0 auto;
		box-sizing: border-box;
		/* ends on the Service Designer step bar's bottom line: the bar is fixed at top 76 px but its
		   v-row margin of -12 px lifts it to 64-124 px, the drawer starts at 64 px, so 60 px */
		height: 60px;
		padding: 0 16px;
		border-bottom: 1px solid #ddd;
	}
	&__title {
		font-weight: 600;
		font-size: 16px;
	}
	&__conversation {
		flex: 1;
		overflow-y: auto;
		padding: 16px;
		display: flex;
		flex-direction: column;
		gap: 8px;
		position: relative;
		&--dragging {
			outline: 2px dashed #7e57c2;
			outline-offset: -6px;
			background: #f3eefa;
		}
	}
	&__dropHint {
		position: sticky;
		top: 0;
		z-index: 1;
		align-self: center;
		padding: 4px 12px;
		border-radius: 12px;
		background: #7e57c2;
		color: white;
		font-size: 12px;
		pointer-events: none;
	}
	&__hint {
		margin: auto;
		text-align: center;
		color: #757575;
		max-width: 280px;
	}
	&__message {
		padding: 8px 12px;
		border-radius: 8px;
		white-space: pre-wrap;
		/* long URLs wrap instead of running out of the bubble */
		overflow-wrap: anywhere;
		max-width: 90%;
		&--user {
			align-self: flex-end;
			background: #e3f2fd;
		}
		&--assistant {
			align-self: flex-start;
			background: #f5f5f5;
		}
		&--system {
			align-self: stretch;
			max-width: none;
			background: #fff8e1;
			border-left: 3px solid #ffb300;
			font-size: 13px;
		}
		&--error {
			align-self: stretch;
			max-width: none;
			background: #fff3f3;
			border-left: 3px solid #c62828;
			color: #c62828;
		}
	}
	&__proposal {
		min-width: 0;
		border: 1px solid #e0e0e0;
		border-radius: 8px;
		padding: 8px 0;
	}
	/* title on its own line, the two actions below it: in German the row was wider than the
	   drawer and cut "Alle zurücksetzen" off */
	&__proposalHeader {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 2px;
		padding: 0 8px 4px 12px;
	}
	&__proposalTitle {
		font-weight: 600;
	}
	&__proposalActions {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		margin-left: -8px;
		max-width: 100%;
	}
	&__groupTitle {
		font-size: 12px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: #757575;
		padding: 8px 12px 2px;
	}
	&__entry {
		display: flex;
		align-items: flex-start;
		border-left: 3px solid transparent;
		&--proposed { border-left-color: #7e57c2; }
		&--pending { border-left-color: #ffb300; }
		&--accepted { border-left-color: #43a047; }
		&--reverted { opacity: 0.55; }
		&--error { background: #fff3f3; }
	}
	&__entryMain {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 2px;
		padding: 6px 8px 6px 12px;
		text-align: left;
		background: none;
		border: 0;
		cursor: pointer;
		font: inherit;
		color: inherit;
		&:hover { background: #fafafa; }
	}
	&__entryField {
		font-weight: 600;
		font-size: 13px;
	}
	&__entryStatus {
		font-size: 11px;
		color: #757575;
	}
	&__entryAssumption {
		align-self: flex-start;
		padding: 0 6px;
		border-radius: 8px;
		font-size: 11px;
		background: #fff8e1;
		color: #8d6e00;
	}
	&__entryValue {
		font-size: 13px;
		word-break: break-word;
		s { color: #9e9e9e; }
	}
	&__entryReason {
		font-size: 12px;
		color: #616161;
	}
	&__entryError {
		font-size: 12px;
		color: #c62828;
	}
	&__entryActions {
		display: flex;
		padding: 4px 4px 0 0;
	}
	&__dropped {
		border-top: 1px dashed #e0e0e0;
		margin-top: 8px;
	}
	&__droppedItem {
		font-size: 12px;
		color: #757575;
		padding: 2px 12px;
		code { font-size: 11px; }
	}
	&__composer {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 12px 16px;
		border-top: 1px solid #e0e0e0;
	}
	&__box {
		display: flex;
		flex-direction: column;
		border: 1px solid #d0d0d0;
		border-radius: 24px;
		padding: 8px 8px 4px 16px;
		background: white;
		cursor: text;
		&:focus-within { border-color: #7e57c2; box-shadow: 0 0 0 1px #7e57c2; }
	}
	&__input {
		:deep(textarea) { padding-top: 4px; padding-bottom: 4px; }
	}
	&__tools {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-left: -8px;
	}
}
</style>
